"use client";

// Maintenance gate — mounted OUTERMOST in the root layout, wrapping AuthProvider.
// The position matters: auth-context's session restore treats a failed /auth/me
// as "no session" and wipes the stored token, so the gate holds the whole tree
// back (renders nothing) until the first /system/status probe settles — under
// maintenance the app below never mounts and nobody's session gets wiped.
//
// Rendering is driven by the module-level maintenance store in lib/api.ts via
// useSyncExternalStore, so a 503 confirmed by ANY request (not just this probe)
// flips the gate in the same tick the store latches.
//
// No admin bypass: everyone sees the same screen (the backend blocks the
// endpoints either way, so an "admin pass-through" would just show broken UI).

import { useEffect, useState, useSyncExternalStore, type ReactNode } from "react";
import Image from "next/image";
import {
  confirmMaintenanceMode,
  getMaintenanceMessage,
  isMaintenanceMode,
  recheckMaintenanceMode,
  subscribeMaintenance,
} from "@/app/lib/api";
import { SpinnerIcon, WarningIcon } from "@/app/components/icons";

export function MaintenanceGate({ children }: { children: ReactNode }) {
  const maintenance = useSyncExternalStore(
    subscribeMaintenance,
    isMaintenanceMode,
    // Server snapshot: the module store always starts false on a fresh load,
    // so server and first client render agree.
    () => false
  );
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    let cancelled = false;
    confirmMaintenanceMode().finally(() => {
      if (!cancelled) setChecked(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (maintenance) return <MaintenanceScreen />;
  // Hold the tree until the first probe settles (see the position note above).
  if (!checked) return null;
  return <>{children}</>;
}

// Full-screen maintenance surface in the app's established vocabulary: the
// shell canvas behind a single floating card, hairline border, amber as the
// warning hue (the tinted-surface family the stepper/stat tiles already use),
// Satoshi for the display heading. No gradients, no ETA, no motion — a quiet,
// static screen is the honest treatment here.
function MaintenanceScreen() {
  // Read at render time: the latch and its message are set together, and the
  // screen only mounts after the latch, so the message is always current.
  const message = getMaintenanceMessage();

  return (
    <div className="flex flex-1 items-center justify-center bg-background px-4 py-10">
      <main className="w-full max-w-md">
        <div className="flex flex-col items-center gap-5 rounded-card border border-border bg-card px-6 py-10 text-center sm:px-10">
          <Image src="/logo.png" alt="JobYab" width={108} height={36} className="h-7 w-auto" />

          <div
            className="flex h-14 w-14 items-center justify-center rounded-pill border border-tile-amber-border bg-tile-amber-bg text-tile-amber-fg"
            aria-hidden="true"
          >
            <WarningIcon className="h-7 w-7" />
          </div>

          <div className="flex flex-col gap-2">
            <h1 className="font-display text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
              We&apos;re performing scheduled maintenance
            </h1>
            <p className="text-sm leading-relaxed text-muted-foreground">
              JobYab is temporarily offline while we work on it. Your data is safe —
              please check back in a little while.
            </p>
          </div>

          {message ? (
            <p
              role="status"
              className="w-full rounded-card border border-tile-amber-border bg-tile-amber-bg px-4 py-3 text-sm leading-relaxed text-tile-amber-fg"
            >
              {message}
            </p>
          ) : null}

          <RetryButton />
        </div>
      </main>
    </div>
  );
}

// Quiet affordance for the moment the flag is lifted: re-probes the status
// endpoint and reveals the app without a manual reload. No progress promises,
// no auto-polling — the retry is the user's signal to check again.
function RetryButton() {
  const [rechecking, setRechecking] = useState(false);

  async function handleRetry() {
    setRechecking(true);
    try {
      await recheckMaintenanceMode();
    } finally {
      setRechecking(false);
    }
  }

  return (
    <button
      type="button"
      onClick={handleRetry}
      disabled={rechecking}
      className="mt-1 inline-flex items-center gap-2 rounded-md border border-border bg-card px-4 py-2 text-sm font-semibold text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
    >
      {rechecking ? <SpinnerIcon className="h-4 w-4" /> : null}
      {rechecking ? "Checking…" : "Try again"}
    </button>
  );
}
